import { Badge, Button, Page, Table } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { MessengerV2 } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';

import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { useMessengers } from 'hooks/useMessengers';
import { useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { AddModal } from './components/addModal/addModal';
import { ListingBody } from './components/body/listingBody';
import { ListingFoot } from './components/foot/listingFoot';
import { RenameModal } from './components/renameModal/renameModal';

type ModalType = 'add' | 'rename' | 'delete';

export const Listing = (): JSX.Element => {
  const notify = useNotifications();
  const navigate = useAppNavigate();
  const messengerApi = useDixaMessengerApi();

  const hasChannelSetupAccess = useHasAccess(
    'settings.integration.channel.setup',
  );

  const {
    items,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    refreshItems,
    total = items.length,
  } = useMessengers({ limit: 125 });

  const [activeModalName, setActiveModalName] = useState<null | ModalType>(
    null,
  );
  const [modalMessengerId, setModalMessengerId] = useState<null | string>(null);

  const showSpecificModal = (messengerId: string | null, modal: ModalType) => {
    ampli.messengerFormOpened({ mode: modal });
    setActiveModalName(modal);
    setModalMessengerId(messengerId);
  };

  const showAddModal = useCallback(() => {
    showSpecificModal(null, 'add');
  }, []);

  const showRenameModal = useCallback((messengerId: string) => {
    ampli.messengerOptionsMenuOptionClicked({ option: 'copy-embed' });
    showSpecificModal(messengerId, 'rename');
  }, []);

  const showDeleteModal = useCallback((messengerId: string) => {
    ampli.messengerOptionsMenuOptionClicked({ option: 'delete' });
    showSpecificModal(messengerId, 'delete');
  }, []);

  const goToFlow = useCallback(
    (flowId: string) => {
      ampli.messengerOptionsMenuOptionClicked({ option: 'edit-flow' });
      navigate({
        path: '/settings/dixa-messenger/flow/:flowId',
        params: { path: { flowId } },
      });
    },
    [navigate],
  );

  const goToAudit = useCallback(
    (flowId: string) => {
      ampli.messengerFlowOptionsMenuOptionAuditLogClicked();
      navigate({
        path: '/settings/dixa-messenger/flow/:flowId/history',
        params: { path: { flowId } },
      });
    },
    [navigate],
  );

  const goToAppearance = useCallback(
    (messengerId: string) => {
      ampli.messengerOptionsMenuOptionClicked({
        option: 'edit-settings',
      });
      navigate({
        path: '/settings/dixa-messenger/settings/:messengerId',
        params: { path: { messengerId } },
      });
    },
    [navigate],
  );

  const closeModal = useCallback(() => {
    if (activeModalName !== null) {
      ampli.messengerFormCancelled({ mode: activeModalName });
    }
    setActiveModalName(null);
    setModalMessengerId(null);
  }, [activeModalName]);

  const messenger =
    items.find((messenger) => messenger.id === modalMessengerId) || null;

  const duplicateMessengerActionCallback = useCallback(
    async (messengerId: string) => {
      try {
        ampli.messengerOptionsMenuOptionClicked({ option: 'duplicate' });
        await messengerApi.duplicate(messengerId);
        refreshItems();
        notify({
          title: 'Messenger duplicated',
          text: 'Messenger was successfully copied',
          icon: 'done',
          className: 'success',
          time: 5000,
        });
      } catch {
        notify({
          title: 'An error occurred',
          text: `An error occurred when trying to duplicate the messenger.`,
          icon: 'exclamation',
          className: 'danger',
        });
      }
    },
    [notify, refreshItems, messengerApi],
  );

  const deleteMessengerModalCallback = useCallback(async () => {
    try {
      if (!modalMessengerId) return;

      ampli.messengerFormSubmitted({ mode: 'delete' });

      await messengerApi.delete(modalMessengerId);

      notify({
        title: 'Messenger deleted',
        text: `Messenger was deleted`,
        icon: 'done',
        className: 'success',
        time: 5000,
      });

      closeModal();
      refreshItems();
    } catch {
      notify({
        title: 'An error occurred',
        text: `An error occurred when trying to delete the messenger.`,
        icon: 'exclamation',
        className: 'danger',
      });
    }
  }, [closeModal, modalMessengerId, notify, refreshItems, messengerApi]);

  const handleNewMessengerAdded = useCallback(
    ({ id }: MessengerV2) =>
      setTimeout(() => {
        navigate({
          path: '/settings/dixa-messenger/settings/:messengerId',
          params: { path: { messengerId: id } },
        });
      }),
    [navigate],
  );

  return (
    <>
      <Page.Section
        title="All messengers"
        titleMeta={
          total && (
            <Badge size="small" variant="secondary">
              {total}
            </Badge>
          )
        }
        controls={
          hasChannelSetupAccess && (
            <Button onClick={showAddModal} id="add-messenger-btn">
              Add messenger
            </Button>
          )
        }
      >
        <Table
          framed
          stickyHeader
          stickyFooter
          headerWithBackground
          loading={asyncStatus === 'loading'}
          loadingStrategy="spinner"
          emptyState={
            asyncStatus === 'succeeded' && !items.length
              ? {
                  title: 'No messenger widgets found',
                  description:
                    'Get started by adding your first messenger widget.',
                }
              : undefined
          }
          onLoadingRetry={refreshItems}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th columnId="name">Name</Table.Th>
              <Table.Th columnId={null} />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            <ListingBody
              items={items}
              onRename={showRenameModal}
              onDelete={showDeleteModal}
              onDuplicateMessenger={duplicateMessengerActionCallback}
              onEditSettings={goToAppearance}
              onEditFlow={goToFlow}
              onAudit={goToAudit}
            />
          </Table.Tbody>

          <ListingFoot
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            nextPage={nextPage}
            previousPage={previousPage}
            asyncStatus={asyncStatus}
          />
        </Table>
      </Page.Section>

      {/** MODALS */}
      <AddModal
        show={activeModalName === 'add'}
        onClose={closeModal}
        onAdd={handleNewMessengerAdded}
      />

      <RenameModal
        show={activeModalName === 'rename' && modalMessengerId !== null}
        messengerId={modalMessengerId}
        initialValues={{ name: messenger?.name || '' }}
        closeModal={closeModal}
        refreshItems={refreshItems}
      />

      <ConfirmModal
        cancel={closeModal}
        cancelText="Cancel"
        confirm={deleteMessengerModalCallback}
        confirmText="Yes, delete it now"
        headline="Are you sure?"
        isShown={activeModalName === 'delete' && modalMessengerId !== null}
        text={`Are you sure you want to delete the messenger
            "${messenger?.name}"?`}
      />
    </>
  );
};
